import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight } from 'lucide-react';

const Logo = () => (
  <div className="flex items-center gap-4">
    <div className="relative w-12 h-12 flex items-center justify-center">
      <svg className="w-full h-full" viewBox="0 0 100 100">
        <defs>
          <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#06b6d4" />
            <stop offset="50%" stopColor="#a855f7" />
            <stop offset="100%" stopColor="#d946ef" />
          </linearGradient>
        </defs>
        {/* V Lettermark */}
        <path 
          d="M20,20 L50,85 L80,20" 
          stroke="url(#logoGradient)" 
          strokeWidth="10" 
          fill="none" 
          strokeLinecap="round" 
        />
        {/* Circuit lines */}
        <path 
          d="M5,25 L15,25 L20,35" 
          stroke="#06b6d4" 
          strokeWidth="2" 
          fill="none" 
          className="circuit-path"
        />
        <path 
          d="M5,45 L10,45 L15,55" 
          stroke="#a855f7" 
          strokeWidth="2" 
          fill="none" 
          className="circuit-path"
          style={{ animationDelay: '1s' }}
        />
        {/* Glowing orb at apex */}
        <circle cx="50" cy="40" r="8" fill="#a855f7" className="animate-pulse shadow-lg shadow-purple-500/50" />
      </svg>
    </div>
    <div className="flex flex-col">
      <div className="flex items-baseline gap-1.5 leading-none">
        <span className="text-xl font-orbitron font-bold tracking-[0.15em] text-white">VEXTORA</span>
        <span className="text-lg font-orbitron font-light tracking-[0.15em] text-gray-400">MEDIA</span>
      </div>
      <span className="text-[8px] font-orbitron font-semibold tracking-[0.4em] text-purple-400 mt-1">ACCELERATE YOUR BRAND</span>
    </div>
  </div>
);

const Navigation: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'How It Works', path: '/how-it-works' },
    { name: 'About', path: '/about' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-[#0A0612]/90 backdrop-blur-xl py-3 border-b border-white/5' : 'bg-transparent py-6'}`}>
      <div className="container mx-auto px-6 flex justify-between items-center">
        <Link to="/">
          <Logo />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-10">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={`text-xs uppercase tracking-widest font-semibold transition-all hover:text-purple-400 ${location.pathname === link.path ? 'text-purple-500' : 'text-gray-400'}`}
            >
              {link.name}
            </Link>
          ))}
          <Link to="/contact">
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(168, 85, 247, 0.4)" }}
              whileTap={{ scale: 0.95 }}
              className="bg-purple-600 text-white px-7 py-2.5 rounded-full text-xs uppercase tracking-widest font-bold flex items-center gap-2 transition-all"
            >
              Get Started
              <ArrowRight size={14} />
            </motion.button>
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button 
          className="lg:hidden text-white" 
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
        >
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            className="fixed inset-0 z-40 bg-[#0A0612] flex flex-col items-center justify-center gap-10 lg:hidden"
          >
            <button 
              className="absolute top-6 right-6 text-white" 
              onClick={() => setIsOpen(false)}
              aria-label="Close navigation menu"
            >
              <X size={32} />
            </button>
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className="text-2xl font-orbitron font-bold text-white hover:text-purple-500 transition-colors tracking-widest"
              >
                {link.name}
              </Link>
            ))}
            <Link to="/contact" onClick={() => setIsOpen(false)}>
              <button className="bg-purple-600 text-white px-10 py-4 rounded-full text-lg font-bold tracking-widest">
                START YOUR BRAND
              </button>
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navigation;